"use client";

import { SwitchButton } from "@commerce-frontend/ui/components/SwitchButton/SwitchButton";
import type { CompanyLinks } from "@commerce-frontend/ui/providers/StaticSiteConfigProvider";
import { useVat } from "@commerce-frontend/ui/providers/VatProvider";
import { getLanguageDisplayName } from "@commerce-frontend/ui/utils/intl";
import { isDealerCustomerGroupKey } from "@commerce-shared/site-config";
import { countryOf, isValue } from "@commerce-shared/utility";
import type { FragmentOf } from "gql.tada";
import { useTranslations } from "next-intl";
import { memo } from "react";
import { useCustomer } from "~/hooks/customer";
import { useStaticUrl } from "~/hooks/static-url";
import { DealerAccountNameIntegration } from "../../../account/_components/DealerAccountName.integration";
import type { AccountMenuItem } from "../../../account/_components/layout/types";
import { AccountMenuItems } from "../AccountMenuItems/AccountMenuItems";
import type { MenuItemFragment } from "../Header.fragments";
import { MenuItemLink } from "./MenuItemLink";
import { MenuItemTrigger } from "./MenuItemTrigger";
import TopBar from "./TopBar";

type Props = {
	topLevelMenuItems: FragmentOf<typeof MenuItemFragment>[];
	staticLinks: CompanyLinks;
	accountLinks: AccountMenuItem[];
	onMainMenuItemSelect: (item: FragmentOf<typeof MenuItemFragment>) => void;
	onLanguageSelect: (languageOpen: boolean) => void;
	locale: string;
};

export const MainMenu = memo(
	({
		topLevelMenuItems,
		staticLinks,
		accountLinks,
		onMainMenuItemSelect,
		onLanguageSelect,
		locale: currentLocale,
	}: Props) => {
		const t = useTranslations("Navigation");
		const loginUrl = useStaticUrl("login");
		const registerUrl = useStaticUrl("register");
		const { customer } = useCustomer();
		const { includingVat, onVatChange, setVatModalIsOpen } = useVat();

		return (
			<>
				<TopBar title={t("menuAccessibilityName")} />

				<div className="max-h-screen flex-1 overflow-y-scroll">
					<nav className="px-4 pt-8">
						<div className="border-b border-border pb-4">
							<strong className="text-main">{t("mobileCategoryLabel")}</strong>
						</div>

						{topLevelMenuItems.map((mainMenuItem) =>
							mainMenuItem.subMenuItems ? (
								<MenuItemTrigger
									key={mainMenuItem.label}
									label={mainMenuItem.label}
									icon={mainMenuItem.icon}
									trigger={() => {
										onMainMenuItemSelect(mainMenuItem);
									}}
								/>
							) : (
								<MenuItemLink
									key={mainMenuItem.label}
									label={mainMenuItem.label}
									link={mainMenuItem.link}
									categoryIcon={mainMenuItem.icon}
								/>
							),
						)}
					</nav>

					<nav className="px-4 pt-8">
						<div className="border-b border-border pb-4">
							{isDealerCustomerGroupKey(customer?.customerGroup?.key) ? (
								<DealerAccountNameIntegration />
							) : (
								<strong className="text-main">{t("mobileMyHbmLabel")}</strong>
							)}
						</div>
						{customer ? (
							<AccountMenuItems items={accountLinks} />
						) : (
							<>
								<MenuItemLink
									label={t("mobileSignIn")}
									link={{ url: loginUrl }}
									icon="login"
								/>
								<MenuItemLink
									label={t("mobileRegister")}
									link={{ url: registerUrl }}
									icon="user-add"
								/>
							</>
						)}
					</nav>

					<nav className="px-4 pt-8">
						<div className="border-b border-border pb-4">
							<strong className="text-main">
								{t("mobileSelfserviceLabel")}
							</strong>
						</div>

						{[staticLinks.service, staticLinks.business]
							.filter(isValue)
							.map((item) => (
								<MenuItemLink
									key={item.label}
									label={item.label}
									link={item.link}
									icon={item.icon}
								/>
							))}

						<MenuItemTrigger
							key={"language_selector"}
							label={
								t("mobileLanguage") +
									": " +
									getLanguageDisplayName(currentLocale) || ""
							}
							flagCountry={countryOf(currentLocale)}
							trigger={() => onLanguageSelect(true)}
							showChevron={false}
						/>
					</nav>

					<div className="px-4 py-8">
						<div className="pb-4">
							<strong className="text-main">{t("vatTitle")}</strong>
						</div>
						<div className="flex items-center gap-2">
							<SwitchButton
								checked={includingVat}
								onChange={onVatChange}
								size={"sm"}
							/>
							<button
								type="button"
								className="text-sm text-main"
								onClick={() => setVatModalIsOpen(true)}
							>
								{t("vatLabel")}
							</button>
						</div>
					</div>
				</div>
			</>
		);
	},
);

MainMenu.displayName = "MainMenu";
